import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import DevicesIcon from '@mui/icons-material/Devices';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Divider,
    IconButton,
    Collapse,
    Box,
    useTheme,
    useMediaQuery,
    Tooltip,
    Badge,
    Alert
} from '@mui/material';
import { fetchAlerts } from '../../store/slices/alertsSlice';
import { fetchTickets, fetchTicket } from '../../store/slices/ticketsSlice';
import { fetchPendingHITLTotal } from '../../store/slices/playbooksSlice';
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Dashboard as DashboardIcon,
    Assignment as AssignmentIcon,
    Business as BusinessIcon,
    ReportProblem as WarningIcon,
    ExitToApp as LogoutIcon,
    Hub as CorrelationsIcon,
    Security as IOCsIcon,
    AutoAwesome as PlaybookIcon,
    Psychology as AIPerformanceIcon,
    LibraryBooks as TemplatesIcon,
    HowToVote as ReviewQueueIcon,
    FitnessCenter as AIGymIcon,
    AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout } from '../../hooks/useLogout';
import {RootState, TicketCategory, TicketPriority, TicketStatus} from '../../types';
import {Logo} from "../Logo";
import {IntegrationAlert} from "../../types";
import NotificationBell from '../client/NotificationBell';

// Sidebar width constants
const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 64;

interface MenuItem {
    id: string;
    label: string;
    icon: React.ComponentType;
    path: string;
    adminOnly?: boolean;
    badge?: string | number;
    badgeColor?: 'info' | 'warning' | 'error' | 'white';
}

interface CollapsibleSidebarProps {
    isAdmin?: boolean;
    onToggle?: (collapsed: boolean) => void;
}

const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
                                                                   isAdmin = false,
                                                                   onToggle
                                                               }) => {
    const theme = useTheme();
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const logout = useLogout();

    const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
    const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
    const [categoryFilter, setCategoryFilter] = useState<TicketCategory | ''>('');

    // Get tickets count for badge (assuming you have this in your store)
    const { tickets } = useSelector((state: RootState) => state.tickets);
    const {alerts} = useSelector((state: RootState) => state.alerts);
    const {correlations} = useSelector((state:RootState) => state.correlations)
    const integrationAlerts: IntegrationAlert[] = useSelector((state: RootState) => state.integrationData.alerts);
    const { clients } = useSelector((state: RootState) => state.clients);
    const pendingHITLTotal = useSelector((state: RootState) => state.playbooks.pendingHITLTotal);

    useEffect(() => {
        const filters: any = {};
        if (statusFilter) filters.status = statusFilter;
        if (priorityFilter) filters.priority = priorityFilter;
        if (categoryFilter) filters.category = categoryFilter;

        dispatch(fetchTickets(filters) as any);
        dispatch(fetchAlerts() as any);
  }, [dispatch]);

    useEffect(() => {
        if (clients.length > 0) {
            dispatch(fetchPendingHITLTotal(clients.map((c) => c.id)) as any);
        }
    }, [clients, dispatch]);

    const openTicketsCount = tickets?.filter(ticket => ticket.status !== 'closed').length || 0;
    const alertsCount = alerts.length || 0
    const integrationAlertsCount: number = integrationAlerts.length || 0
    const correlationsCount:number = correlations?.filter(correlation => correlation.status != 'dismissed').length || 0;

    // Get selected alert/ticket to auto-collapse sidebar
    const { selectedAlert } = useSelector((state: RootState) => state.alerts);
    const { selectedTicket } = useSelector((state: RootState) => state.tickets);

    // State management
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Auto-collapse on mobile
    useEffect(() => {
        if (isMobile && !collapsed) {
            setCollapsed(true);
        }
    }, [isMobile]);

    // Auto-collapse when details panel opens
    useEffect(() => {
        if ((selectedAlert || selectedTicket) && !collapsed) {
            setCollapsed(true);
            onToggle?.(true);
        }
    }, [selectedAlert, selectedTicket]);

    // Menu configuration
    const menuItems: MenuItem[] = [
        {
            id: 'overview',
            label: 'Overview',
            icon: DashboardIcon,
            path: '/',
        },
        {
            id: 'alerts',
            label: 'Alerts',
            icon: WarningIcon,
            path: '/alerts',
            adminOnly: true,
            badge: alertsCount > 0 ? alertsCount : undefined,
            badgeColor: 'white' as const,
        },
        // {
        //     id: 'integration-alerts',
        //     label: 'Integration Alerts',
        //     icon: WarningIcon,
        //     path: '/integration-alerts',
        //     adminOnly: true,
        //     badge: integrationAlertsCount > 0 ? integrationAlertsCount : undefined,
        //     badgeColor: 'white' as const,
        // },
        {
            id: 'tickets',
            label: 'Tickets',
            icon: AssignmentIcon,
            path: '/tickets',
            badge: openTicketsCount > 0 ? openTicketsCount : undefined,
            badgeColor: 'white',
        },
        {
            id: 'correlations',
            label: 'Correlations',
            icon: CorrelationsIcon,
            path: '/correlations',
            badge: correlationsCount > 0 ? correlationsCount : undefined,
            adminOnly: true,
        },
        {
            id: 'iocs',
            label: 'IOCs',
            icon: IOCsIcon,
            path: '/iocs',
            adminOnly: true,
        },
        {
            id: 'playbooks',
            label: 'Automation Playbooks',
            icon: PlaybookIcon,
            path: '/playbooks',
            adminOnly: true,
        },
        {
            id: 'playbook-templates',
            label: 'Playbook Templates',
            icon: TemplatesIcon,
            path: '/playbooks/templates',
            adminOnly: true,
        },
        {
            id: 'review-queue',
            label: 'Review Queue',
            icon: ReviewQueueIcon,
            path: '/playbooks/review-queue',
            adminOnly: true,
            badge: pendingHITLTotal > 0 ? pendingHITLTotal : undefined,
            badgeColor: 'error' as const,
        },
        {
            id: 'ai-gym',
            label: 'AI Gym',
            icon: AIGymIcon,
            path: '/playbooks/ai-gym',
            adminOnly: true,
        },
        {
            id: 'ai-performance',
            label: 'AI Performance',
            icon: AIPerformanceIcon,
            path: '/ai-performance',
            adminOnly: true,
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: AdminIcon,
            path: '/admin',
            adminOnly: true,
        },
        // {
        //     id: 'assetInventory',
        //     label: 'Asset Inventory',
        //     icon: DevicesIcon,
        //     path: '/assets-inventory',
        //     adminOnly: true,
        //     // badge: alertsCount > 0 ? alertsCount : undefined,
        //     // badgeColor: 'white' as const,
        // },
        ...(isAdmin ? [{
            id: 'clients',
            label: 'Client List',
            icon: BusinessIcon,
            path: '/clients',
            adminOnly: true,
        }] : []),
    ];

    // Event handlers
    const handleToggle = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        onToggle?.(newCollapsed);
    };

    const handleMobileToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleNavigate = (path: string) => {
        navigate(path);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const handleLogout = () => logout();

    const handleNotificationTicketNavigate = (ticketId: string) => {
        navigate('/tickets');
        dispatch(fetchTicket(ticketId) as any);
    };

    // Several sidebar items share a path prefix (e.g. /playbooks, /playbooks/ai-gym,
    // /playbooks/templates) — a plain startsWith check would highlight all of them at
    // once. Pick only the single most specific (longest) matching item instead.
    //
    // Session detail (/playbooks/sessions/:id) is a special case: it's a drill-down page
    // reached from Review Queue and AI Gym, never from the Automation Playbooks list —
    // so the generic "longest prefix" rule would wrongly attribute it to /playbooks.
    const PATH_OVERRIDES: { test: RegExp; id: string }[] = [
        { test: /^\/playbooks\/sessions\//, id: 'review-queue' },
    ];

    const activeItemId = useMemo(() => {
        const pathname = location.pathname;

        const override = PATH_OVERRIDES.find(o => o.test.test(pathname));
        if (override) return override.id;

        let bestId: string | null = null;
        let bestLength = -1;
        for (const item of menuItems) {
            const matches = item.path === '/'
                ? pathname === '/'
                : pathname === item.path || pathname.startsWith(item.path + '/');
            if (matches && item.path.length > bestLength) {
                bestId = item.id;
                bestLength = item.path.length;
            }
        }
        return bestId;
    }, [location.pathname, menuItems]);

    const isActive = (item: MenuItem) => item.id === activeItemId;

    // Get badge color based on type
    const getBadgeColors = (badgeColor?: 'info' | 'warning' | 'error' | 'white') => {
        switch (badgeColor) {
            case 'info':
                return {
                    bg: 'oklch(65% 0.18 250 / 15%)',
                    color: theme.palette.info.main,
                    border: 'oklch(65% 0.18 250 / 30%)',
                    dot: theme.palette.info.main,
                };
            case 'warning':
                return {
                    bg: 'oklch(75% 0.18 75 / 15%)',
                    color: theme.palette.warning.main,
                    border: 'oklch(75% 0.18 75 / 30%)',
                    dot: theme.palette.warning.main,
                };
            case 'error':
                return {
                    bg: 'oklch(62% 0.22 25 / 15%)',
                    color: theme.palette.error.main,
                    border: 'oklch(62% 0.22 25 / 30%)',
                    dot: theme.palette.error.main,
                };
            case 'white':
                return {
                    bg: 'oklch(100% 0 0 / 10%)',
                    color: theme.palette.text.primary,
                    border: 'oklch(100% 0 0 / 20%)',
                    dot: theme.palette.text.primary,
                }
            default:
                return {
                    bg: theme.palette.glow,
                    color: theme.palette.primary.main,
                    border: 'oklch(60% 0.22 291 / 40%)',
                    dot: theme.palette.primary.main,
                };
        }
    };

    // Render menu item
    const renderMenuItem = (item: MenuItem) => {
        const active = isActive(item);
        const badgeColors = getBadgeColors(item.badgeColor);

        const menuItem = (
            <ListItem
                key={item.id}
                disablePadding
                sx={{
                    display: 'block',
                    mb: 0.5,
                }}
            >
                <ListItemButton
                    onClick={() => handleNavigate(item.path)}
                    disableRipple
                    sx={{
                        minHeight: 48,
                        // Remove fixed margins and padding for better centering
                        px: collapsed ? 0 : 2.5,
                        mx: collapsed ? 0.5 : 1,
                        borderRadius: 1,
                        backgroundColor: active ? theme.palette.interactive.selected : 'transparent',
                        color: active ? theme.palette.text.primary : theme.palette.text.secondary,
                        boxShadow: !collapsed && active ? `inset 3px 0 0 ${theme.palette.primary.main}` : 'none',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        border: 'none',
                        outline: 'none',
                        justifyContent: collapsed ? 'center' : 'flex-start',

                        '&:hover': {
                            backgroundColor: theme.palette.interactive.hover,
                            color: theme.palette.text.primary,
                            transform: collapsed ? 'scale(1.1)' : 'translateX(2px)',
                        },

                        '&:active': {
                            transform: collapsed ? 'scale(1.05)' : 'translateX(1px)',
                        },
                    }}

                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: collapsed ? 0 : 3,
                            justifyContent: 'center',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            fontSize: '1.25rem',
                            color: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            width: collapsed ? '100%' : 'auto',
                        }}
                    >
                        <Box sx={{ fontSize: 'inherit', display: 'flex' }}>
                            <item.icon />
                        </Box>
                    </ListItemIcon>

                    {/* Text and Badge for Expanded State */}
                    {!collapsed && (
                        <>
                            <ListItemText
                                primary={item.label}
                                sx={{
                                    '& .MuiListItemText-primary': {
                                        fontWeight: active ? 700 : 500,
                                        fontSize: '0.9rem',
                                        letterSpacing: '0.02em',
                                    }
                                }}
                            />

                            {/* Badge in Expanded State */}
                            {item.badge && (
                                <Typography
                                    component="span"
                                    sx={{
                                        backgroundColor: theme.palette.background.paper,
                                        color: theme.palette.text.primary,
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.25,
                                        minWidth: 28,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        ml: 'auto',
                                    }}
                                >
                                    {typeof item.badge === 'number' && item.badge > 99 ? '99+' : item.badge}
                                </Typography>
                            )}
                        </>
                    )}

                    {/* Badge for Collapsed State - Small Dot Indicator */}
                    {item.badge && collapsed && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: badgeColors.dot,
                                borderRadius: '50%',
                                width: 8,
                                height: 8,
                                boxShadow: `0 0 0 2px ${theme.palette.background.default}`,
                            }}
                        />
                    )}
                </ListItemButton>
            </ListItem>
        );

        // Wrap with tooltip when collapsed
        if (collapsed) {
            return (
                <Tooltip
                    key={item.id}
                    title={`${item.label}${item.badge ? ` (${item.badge})` : ''}`}
                    placement="right"
                    arrow
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -14],
                                    },
                                },
                            ],
                        },
                        tooltip: {
                            sx: {
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                border: `1px solid ${theme.palette.border.main}`,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                borderRadius: 1,
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                            }
                        },
                        arrow: {
                            sx: {
                                color: theme.palette.background.paper,
                                '&::before': {
                                    border: `1px solid ${theme.palette.border.main}`,
                                }
                            }
                        }
                    }}
                >
                    {menuItem}
                </Tooltip>
            );
        }

        return menuItem;
    };

    // Drawer content
    const drawerContent = (
        <Box className="collapsible-sidebar"
             sx={{
                 height: '100%',
                 display: 'flex',
                 flexDirection: 'column',
                 background: theme.palette.background.default,
             }}
        >
            {/* Header */}
            <Toolbar
                sx={{
                    px: 2,
                    justifyContent: collapsed ? 'center' : 'space-between',
                    backgroundColor: 'transparent',
                    minHeight: '64px !important',
                    borderBottom: `1px solid ${theme.palette.border.light}`,
                }}
            >
                <Collapse in={!collapsed} timeout={300} orientation="horizontal">

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Logo
                            size={36}
                            primaryColor="oklch(60% 0.22 291)"
                            accentColor="oklch(75% 0.10 291)"
                        />
                    </Box>

                    {/*<Typography*/}
                    {/*    variant="h5"*/}
                    {/*    noWrap*/}
                    {/*    sx={{*/}
                    {/*        fontWeight: 800,*/}
                    {/*        //background: colors.dark.primary[800],*/}
                    {/*        background: colors.dark.primary[500],*/}
                    {/*        backgroundClip: 'text',*/}
                    {/*        WebkitBackgroundClip: 'text',*/}
                    {/*        WebkitTextFillColor: 'transparent',*/}
                    {/*        fontSize: '1.4rem',*/}
                    {/*        letterSpacing: '-0.02em',*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    SOCmate*/}
                    {/*</Typography>*/}
                </Collapse>

                {!isMobile && (
                    <IconButton
                        onClick={handleToggle}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            borderRadius: 1,
                            width: 32,
                            height: 32,
                            '&:hover': {
                                backgroundColor: theme.palette.interactive.hover,
                                color: theme.palette.text.primary,
                            },
                            transition: 'color 0.2s, background-color 0.2s',
                        }}
                    >
                        {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                    </IconButton>
                )}
            </Toolbar>

            {/* Menu Items */}
            <Box sx={{ flexGrow: 1, py: 2 }}>
                <List sx={{ px: 0 }}>
                    {menuItems.map(renderMenuItem)}
                </List>
            </Box>

            {/* Notification bell */}
            <Box sx={{ pb: 1 }}>
                <NotificationBell
                    collapsed={collapsed}
                    ticketBasePath="/tickets"
                    onTicketNavigate={handleNotificationTicketNavigate}
                />
            </Box>

            {/* Footer */}
            <Box sx={{ px: 1, pt: 1, pb: 3 }}>
                {/* Logout button */}
                <Tooltip
                    title={collapsed ? 'Logout' : ''}
                    placement="right"
                    arrow
                    slotProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                border: `1px solid ${theme.palette.border.main}`,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                borderRadius: 1,
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                            }
                        },
                        arrow: {
                            sx: {
                                color: theme.palette.background.paper,
                                '&::before': { border: `1px solid ${theme.palette.border.main}` }
                            }
                        }
                    }}
                >
                    <ListItemButton
                        onClick={handleLogout}
                        disableRipple
                        sx={{
                            minHeight: 44,
                            px: collapsed ? 0 : 2.5,
                            mx: collapsed ? 0.5 : 1,
                            borderRadius: 1,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            color: theme.palette.error.main,
                            '&:hover': {
                                backgroundColor: `${theme.palette.error.main}18`,
                                transform: collapsed ? 'scale(1.1)' : 'translateX(4px)',
                            },
                            '&:active': {
                                transform: collapsed ? 'scale(1.05)' : 'translateX(2px)',
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: collapsed ? 0 : 3,
                                justifyContent: 'center',
                                color: 'inherit',
                                display: 'flex',
                                alignItems: 'center',
                                width: collapsed ? '100%' : 'auto',
                            }}
                        >
                            <LogoutIcon />
                        </ListItemIcon>
                        {!collapsed && (
                            <ListItemText
                                primary="Logout"
                                sx={{
                                    '& .MuiListItemText-primary': {
                                        fontWeight: 500,
                                        fontSize: '0.9rem',
                                        letterSpacing: '0.02em',
                                    }
                                }}
                            />
                        )}
                    </ListItemButton>
                </Tooltip>

                {/* Version info */}
                <Box sx={{ px: collapsed ? 0 : 1.5, pt: 1, textAlign: collapsed ? 'center' : 'left' }}>
                    {collapsed ? (
                        <Tooltip title="SOC Dashboard v1.0" placement="right" arrow>
                            <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontSize: '0.65rem', fontWeight: 600 }}>
                                v1.0
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.primary, display: 'block', lineHeight: 1.2, fontWeight: 500 }}>
                                SOC Dashboard v1.0.0
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontSize: '0.65rem', fontWeight: 600 }}>
                                {isAdmin ? 'Administrator' : 'Operator'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    return (
        <>
            {/* Mobile menu button - positioned in the app bar */}
            {isMobile && (
                <IconButton
                    color="inherit"
                    edge="start"
                    onClick={handleMobileToggle}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        left: 16,
                        zIndex: theme.zIndex.appBar + 1,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.secondary,
                        // border: `1px solid ${theme.palette.border.light}`,
                        borderRadius: 1,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                        }
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            {/* Desktop drawer */}
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            backgroundColor: theme.palette.background.default,
                            // borderRight: `1px solid ${theme.palette.border.light}`,
                            borderRadius: 0,
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.standard,
                            }),
                            overflowX: 'hidden',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* Mobile drawer */}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleMobileToggle}
                    ModalProps={{
                        keepMounted: true, // Better performance on mobile
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            backgroundColor: theme.palette.background.default,
                            border: 'none',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

export default CollapsibleSidebar;